import {
  type GraphPathLookupUnicastParameters,
  type GraphPathLookupUnicastParametersBase,
  isAutomaticFirstHop,
  isUserDefinedFirstHop,
} from '@ipfabric/utils';
import { isVrfAutodetect } from 'components/Graph/GraphSidePanel/GraphPathlookup/helpers/isVrfAutodetect';
import { PATH_LOOKUP_GROUP_BY } from 'components/Graph/model/pathLookup';
import {
  type GraphAdvancedOptionsFormValues,
  advancedOptionsToFormValues,
  formValuesToAdvancedOptions,
} from './PathLookupAdvancedOptionsFormValues';
import type { Vrf } from './fetchVrf';
import {
  type EntryPointValue,
  entryPointValuesToEntryPoints,
  entryPontsToEntryPointValues,
} from './helpers/entryPoint';

export interface UnicastFormValues extends GraphAdvancedOptionsFormValues {
  destinationPoint: string;
  firstHopAlgorithm: 'automatic' | 'userDefined';
  hostMode: boolean;
  securedPath: boolean;
  startingPoint: string;
  vrf: Vrf | null;
  entryPoints: EntryPointValue[];
}

export const unicastFormValuesToParameters = (
  values: UnicastFormValues,
): GraphPathLookupUnicastParameters => {
  const firstHopAlgorithm =
    values.firstHopAlgorithm === 'automatic'
      ? {
          type: values.firstHopAlgorithm,
          ...(!isVrfAutodetect(values.vrf) && { vrf: values.vrf }),
        }
      : {
          type: values.firstHopAlgorithm,
          entryPoints: entryPointValuesToEntryPoints(values.entryPoints),
        };

  const endpointValues: GraphPathLookupUnicastParametersBase = {
    destinationPoint: values.destinationPoint,
    groupBy: PATH_LOOKUP_GROUP_BY,
    networkMode: !values.hostMode,
    pathLookupType: 'unicast',
    securedPath: values.securedPath,
    startingPoint: values.startingPoint,
    type: 'pathLookup',
    firstHopAlgorithm,
  } as const;

  const advancedOptions = {
    enableRegions: values.enableRegions,
    protocol: values.protocol,
    srcRegions: values.srcRegions,
    dstRegions: values.dstRegions,
    ttl: values.ttl,
    fragmentOffset: values.fragmentOffset,
    applications: values.applications,
    applicationsMatchWithRegex: values.applicationsMatchWithRegex,
    category: values.category,
    tracked: values.tracked,
    srcPorts: values.srcPorts,
    dstPorts: values.dstPorts,
    icmpOptions: values.icmpOptions,
    flags: values.flags,
    url: values.url,
  };

  return {
    ...endpointValues,
    ...formValuesToAdvancedOptions(advancedOptions),
  };
};

export const unicastParametersToFormValues = (
  parameters: GraphPathLookupUnicastParameters,
): UnicastFormValues => {
  const commonValues = {
    destinationPoint: parameters.destinationPoint,
    firstHopAlgorithm: parameters.firstHopAlgorithm.type,
    hostMode: !parameters.networkMode,
    securedPath: parameters.securedPath,
    startingPoint: parameters.startingPoint,
    vrf: isAutomaticFirstHop(parameters.firstHopAlgorithm)
      ? (parameters.firstHopAlgorithm.vrf ?? null)
      : null,
    entryPoints: isUserDefinedFirstHop(parameters.firstHopAlgorithm)
      ? entryPontsToEntryPointValues(parameters.firstHopAlgorithm.entryPoints)
      : [{ host: null, iface: null }],
  } as const;

  return {
    ...commonValues,
    ...advancedOptionsToFormValues(parameters),
  };
};
